import React from 'react';
import { Text, TouchableOpacity, StyleSheet, View } from 'react-native';
import type { LucideIcon } from 'lucide-react-native';

type ButtonVariant = 'primary' | 'secondary' | 'danger';
type ButtonSize = 'large' | 'medium';

interface FriendlyButtonProps {
    variant?: ButtonVariant;
    size?: ButtonSize;
    icon?: LucideIcon;
    onPress: () => void;
    disabled?: boolean;
    children: string;
    testID?: string;
    accessibilityLabel?: string;
    accessibilityHint?: string;
}

const variantStyles: Record<ButtonVariant, {
    backgroundColor: string;
    color: string;
    shadowColor: string;
    borderColor?: string;
    borderWidth?: number;
}> = {
    primary: {
        backgroundColor: '#52C41A',
        color: '#FFFFFF',
        shadowColor: '#52C41A',
    },
    secondary: {
        backgroundColor: '#FFFFFF',
        color: '#52C41A',
        shadowColor: '#000000',
        borderColor: '#52C41A',
        borderWidth: 2,
    },
    danger: {
        backgroundColor: '#F5222D',
        color: '#FFFFFF',
        shadowColor: '#F5222D',
    },
};

const sizeStyles: Record<ButtonSize, {
    minHeight: number;
    fontSize: number;
    paddingHorizontal: number;
    paddingVertical: number;
    iconSize: number;
}> = {
    large: {
        minHeight: 76,
        fontSize: 24,
        paddingHorizontal: 40,
        paddingVertical: 22,
        iconSize: 32,
    },
    medium: {
        minHeight: 56,
        fontSize: 18,
        paddingHorizontal: 32,
        paddingVertical: 16,
        iconSize: 24,
    },
};

export function FriendlyButton({
    variant = 'primary',
    size = 'large',
    icon: Icon,
    onPress,
    disabled = false,
    children,
    testID,
    accessibilityLabel,
    accessibilityHint,
}: FriendlyButtonProps) {
    const vStyle = variantStyles[variant];
    const sStyle = sizeStyles[size];

    return (
        <TouchableOpacity
            testID={testID}
            accessibilityLabel={accessibilityLabel || children}
            accessibilityRole="button"
            accessibilityHint={accessibilityHint || `Double tap to ${children}`}
            accessibilityState={{ disabled }}
            onPress={onPress}
            disabled={disabled}
            activeOpacity={0.75}
            style={[
                styles.button,
                {
                    backgroundColor: disabled ? '#94A3B8' : vStyle.backgroundColor,
                    minHeight: sStyle.minHeight,
                    paddingHorizontal: sStyle.paddingHorizontal,
                    paddingVertical: sStyle.paddingVertical,
                    shadowColor: disabled ? '#94A3B8' : vStyle.shadowColor,
                    borderColor: vStyle.borderColor,
                    borderWidth: vStyle.borderWidth || 0,
                },
            ]}
        >
            {Icon && (
                <View style={styles.iconContainer}>
                    <Icon size={sStyle.iconSize} color={disabled ? '#FFFFFF' : vStyle.color} strokeWidth={3} />
                </View>
            )}
            <Text
                allowFontScaling={true}
                maxFontSizeMultiplier={1.5}
                style={[
                    styles.text,
                    {
                        color: disabled ? '#FFFFFF' : vStyle.color,
                        fontSize: sStyle.fontSize,
                    },
                ]}
            >
                {children}
            </Text>
        </TouchableOpacity>
    );
}

const styles = StyleSheet.create({
    button: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 20,
        gap: 12,
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.35,
        shadowRadius: 12,
        elevation: 8,
        alignSelf: 'stretch',
    },
    iconContainer: {
        justifyContent: 'center',
        alignItems: 'center',
    },
    text: {
        fontWeight: '900',
        textAlign: 'center',
    },
});
